import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import {
  CALLABLE_BEER_CLASSIFY_BEER_INGESTION_BREWERY,
  CALLABLE_BEER_EXCLUDE_BEER_INGESTION_BREWERY,
  CALLABLE_BEER_LIST_BEER_INGESTION,
  CALLABLE_BEER_LIST_INGESTION_REVIEWS,
  CALLABLE_BEER_LIST_INGESTION_FAILURES,
  CALLABLE_BEER_UPDATE_INGESTION_FAILURE_DISPOSITION,
  CALLABLE_BEER_RETRY_INGESTION_FAILURE,
  CALLABLE_BEER_DECIDE_INGESTION_CANDIDATE,
  CALLABLE_BEER_BULK_APPROVE_INGESTION,
  CALLABLE_BEER_BULK_REJECT_INGESTION,
  CALLABLE_BEER_COMPLETE_INGESTION_REVIEW,
  CALLABLE_BEER_APPLY_INGESTION_REVIEW,
  BEER_INGESTION_TRUST_TARGET_PERCENT,
  FRESHNESS_TARGET_PERCENT,
  REVIEWABLE_TARGET_PERCENT,
  PILOT_BUDGET_CREDITS,
} from '../utils/callableBeerConstants';
import {
  EMPTY_BEER_INGESTION_SUMMARY,
  isRetiredBeerSource,
  listedBeerIngestionSources,
} from '../utils/brewery/beerIngestionStatus';
import { processImportResponse } from '../utils/addBeerResponse';
import { isIngestionPipeSource } from '../utils/brewery/breweryBeerSourceFilters';

function parseIsoDate(value) {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeSourceRecord(rawSource = {}) {
  const sourceType =
    typeof rawSource.sourceType === 'string' && rawSource.sourceType.trim()
      ? rawSource.sourceType.trim()
      : null;
  return {
    ...rawSource,
    url: rawSource.url || rawSource.source || '',
    sourceType,
    createdAt: parseIsoDate(rawSource.createdAt),
    updatedAt: parseIsoDate(rawSource.updatedAt),
    lastCheckedAt: parseIsoDate(rawSource.lastCheckedAt),
    lastChangedAt: parseIsoDate(rawSource.lastChangedAt),
    nextCheckAt: parseIsoDate(rawSource.nextCheckAt),
    pendingManualCheckRequestedAt: parseIsoDate(rawSource.pendingManualCheckRequestedAt),
    lastExtractBeerCount: normalizeExtractBeerCount(rawSource.lastExtractBeerCount),
  };
}

function normalizeExtractBeerCount(value) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return null;
  }
  return Math.floor(value);
}

function normalizeClassificationRecord(rawClassification) {
  if (!rawClassification) {
    return null;
  }
  return {
    ...rawClassification,
    classifiedAt: parseIsoDate(rawClassification.classifiedAt),
  };
}

function normalizeSummary(rawSummary) {
  if (!rawSummary || typeof rawSummary !== 'object') {
    return { ...EMPTY_BEER_INGESTION_SUMMARY };
  }
  return {
    totalInScope: Number(rawSummary.totalInScope) || 0,
    trusted: Number(rawSummary.trusted) || 0,
    needsSource: Number(rawSummary.needsSource) || 0,
    noSuitableSource: Number(rawSummary.noSuitableSource) || 0,
    excluded: Number(rawSummary.excluded) || 0,
    trustedPercent: Number(rawSummary.trustedPercent) || 0,
    increment1ExceptionCount: Number(rawSummary.increment1ExceptionCount) || 0,
    increment1Denominator: Number(rawSummary.increment1Denominator) || 0,
  };
}

function asFiniteNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizePilotBar(rawBar, fallbackTarget) {
  const bar = rawBar && typeof rawBar === 'object' ? rawBar : {};
  return {
    numerator: asFiniteNumber(bar.numerator),
    denominator: asFiniteNumber(bar.denominator),
    percent: asFiniteNumber(bar.percent),
    targetPercent: asFiniteNumber(bar.targetPercent, fallbackTarget),
    met: bar.met === true,
  };
}

function normalizeApplyRequiresApproval(value) {
  if (typeof value === 'boolean') {
    return value;
  }
  return null;
}

function normalizePilotScoreboard(rawScoreboard) {
  if (!rawScoreboard || typeof rawScoreboard !== 'object') {
    return null;
  }
  const trusted = rawScoreboard.trusted && typeof rawScoreboard.trusted === 'object'
    ? rawScoreboard.trusted
    : {};
  const cost = rawScoreboard.cost && typeof rawScoreboard.cost === 'object'
    ? rawScoreboard.cost
    : {};
  const representation = rawScoreboard.representation && typeof rawScoreboard.representation === 'object'
    ? rawScoreboard.representation
    : {};
  const reviewEffort = rawScoreboard.reviewEffort && typeof rawScoreboard.reviewEffort === 'object'
    ? rawScoreboard.reviewEffort
    : {};
  const assertions = rawScoreboard.assertions && typeof rawScoreboard.assertions === 'object'
    ? rawScoreboard.assertions
    : {};
  const medianRaw = reviewEffort.medianReviewMinutes;
  const medianReviewMinutes = medianRaw === null || medianRaw === undefined
    ? null
    : asFiniteNumber(medianRaw, null);

  return {
    windowStartIso: typeof rawScoreboard.windowStartIso === 'string'
      ? rawScoreboard.windowStartIso
      : '',
    windowEndIso: typeof rawScoreboard.windowEndIso === 'string'
      ? rawScoreboard.windowEndIso
      : '',
    computedAtIso: typeof rawScoreboard.computedAtIso === 'string'
      ? rawScoreboard.computedAtIso
      : '',
    trusted: {
      ...normalizePilotBar(trusted, BEER_INGESTION_TRUST_TARGET_PERCENT),
      exceptionCount: asFiniteNumber(trusted.exceptionCount),
    },
    freshness: normalizePilotBar(rawScoreboard.freshness, FRESHNESS_TARGET_PERCENT),
    reviewable: normalizePilotBar(rawScoreboard.reviewable, REVIEWABLE_TARGET_PERCENT),
    cost: {
      creditsTotal: asFiniteNumber(cost.creditsTotal),
      budgetCredits: asFiniteNumber(cost.budgetCredits, PILOT_BUDGET_CREDITS),
      creditsPerMonitoredBrewery: asFiniteNumber(cost.creditsPerMonitoredBrewery),
      monitoredBreweryCount: asFiniteNumber(cost.monitoredBreweryCount),
      met: cost.met === true,
      monitorCheckCreditsStored: false,
    },
    representation: {
      excluded: asFiniteNumber(representation.excluded),
      needsSource: asFiniteNumber(representation.needsSource),
      noSuitableSource: asFiniteNumber(representation.noSuitableSource),
      increment1Exceptions: asFiniteNumber(representation.increment1Exceptions),
      increment1ExceptionsByReason:
        representation.increment1ExceptionsByReason
        && typeof representation.increment1ExceptionsByReason === 'object'
          ? representation.increment1ExceptionsByReason
          : {},
      openFailures: asFiniteNumber(representation.openFailures),
      autoPaused: asFiniteNumber(representation.autoPaused),
    },
    reviewEffort: {
      medianReviewMinutes,
      completedReviewCount: asFiniteNumber(reviewEffort.completedReviewCount),
      newCandidateCount: asFiniteNumber(reviewEffort.newCandidateCount),
    },
    assertions: {
      applyRequiresApproval: normalizeApplyRequiresApproval(
        assertions.applyRequiresApproval,
      ),
    },
  };
}

function normalizeListRow(rawRow = {}) {
  const sources = listedBeerIngestionSources(
    Array.isArray(rawRow.sources) ? rawRow.sources.map(normalizeSourceRecord) : [],
  );
  const trustedSource = rawRow.trustedSource
    ? normalizeSourceRecord(rawRow.trustedSource)
    : null;
  return {
    ...rawRow,
    sources,
    classification: normalizeClassificationRecord(rawRow.classification),
    trustedSource:
      trustedSource && isIngestionPipeSource(trustedSource) && !isRetiredBeerSource(trustedSource)
        ? trustedSource
        : null,
  };
}

export async function listBeerIngestion(stateCode, options = {}) {
  const includePilotScoreboard = options.includePilotScoreboard === true;
  const listFn = includePilotScoreboard
    ? httpsCallable(functions, CALLABLE_BEER_LIST_BEER_INGESTION, { timeout: 120000 })
    : httpsCallable(functions, CALLABLE_BEER_LIST_BEER_INGESTION);
  const payload = includePilotScoreboard
    ? { stateCode, includePilotScoreboard: true }
    : { stateCode };
  const result = await listFn(payload);
  const data = result.data ?? {};
  const rows = Array.isArray(data.rows) ? data.rows.map(normalizeListRow) : [];
  return {
    rows,
    summary: normalizeSummary(data.summary),
    pilotScoreboard: normalizePilotScoreboard(data.pilotScoreboard),
  };
}

export async function classifyBeerIngestionBrewery(payload) {
  const classifyFn = httpsCallable(
    functions,
    CALLABLE_BEER_CLASSIFY_BEER_INGESTION_BREWERY,
  );
  const result = await classifyFn(payload);
  const data = result.data ?? {};
  return normalizeClassificationRecord(data.classification) ?? {};
}

export async function excludeBeerIngestionBrewery(payload) {
  const excludeFn = httpsCallable(
    functions,
    CALLABLE_BEER_EXCLUDE_BEER_INGESTION_BREWERY,
  );
  const result = await excludeFn(payload);
  const data = result.data ?? {};
  return normalizeClassificationRecord(data.classification) ?? {};
}

function normalizeReviewQueueRow(rawRow = {}) {
  const queuePhase = rawRow.queuePhase === 'apply' ? 'apply' : 'review';
  return {
    ...rawRow,
    startedAt: parseIsoDate(rawRow.startedAt),
    sourceId: rawRow.sourceId ?? null,
    reviewPendingCount: Number(rawRow.reviewPendingCount) || 0,
    reviewDecidedCount: Number(rawRow.reviewDecidedCount) || 0,
    reviewApprovedCount: Number(rawRow.reviewApprovedCount) || 0,
    candidatesNewCount: Number(rawRow.candidatesNewCount) || 0,
    candidatesChangedCount: Number(rawRow.candidatesChangedCount) || 0,
    candidatesRenameCount: Number(rawRow.candidatesRenameCount) || 0,
    candidatesMissingCount: Number(rawRow.candidatesMissingCount) || 0,
    candidatesInvalidCount: Number(rawRow.candidatesInvalidCount) || 0,
    runKind: rawRow.runKind ?? 'import',
    applyStatus: rawRow.applyStatus ?? null,
    queuePhase,
  };
}

function normalizeReviewSummary(rawSummary) {
  if (!rawSummary || typeof rawSummary !== 'object') {
    return {
      pendingRuns: 0,
      inProgressRuns: 0,
      pendingCandidates: 0,
      unappliedRuns: 0,
    };
  }
  return {
    pendingRuns: Number(rawSummary.pendingRuns) || 0,
    inProgressRuns: Number(rawSummary.inProgressRuns) || 0,
    pendingCandidates: Number(rawSummary.pendingCandidates) || 0,
    unappliedRuns: Number(rawSummary.unappliedRuns) || 0,
  };
}

function normalizeReviewRecord(rawReview) {
  if (!rawReview || typeof rawReview !== 'object') {
    return null;
  }
  const rawDecisions = rawReview.decisions && typeof rawReview.decisions === 'object'
    ? rawReview.decisions
    : {};
  const decisions = {};
  Object.entries(rawDecisions).forEach(([candidateKey, decision]) => {
    decisions[candidateKey] = {
      ...decision,
      decidedAt: parseIsoDate(decision?.decidedAt),
    };
  });
  return {
    ...rawReview,
    decisions,
    createdAt: parseIsoDate(rawReview.createdAt),
    updatedAt: parseIsoDate(rawReview.updatedAt),
    completedAt: parseIsoDate(rawReview.completedAt),
    applyStartedAt: parseIsoDate(rawReview.applyStartedAt),
    applyCompletedAt: parseIsoDate(rawReview.applyCompletedAt),
  };
}

export async function listIngestionReviews(stateCode) {
  const listFn = httpsCallable(functions, CALLABLE_BEER_LIST_INGESTION_REVIEWS);
  const result = await listFn({ stateCode });
  const data = result.data ?? {};
  const rows = Array.isArray(data.rows) ? data.rows.map(normalizeReviewQueueRow) : [];
  return {
    rows,
    summary: normalizeReviewSummary(data.summary),
  };
}

export async function decideIngestionCandidate(payload) {
  const decideFn = httpsCallable(functions, CALLABLE_BEER_DECIDE_INGESTION_CANDIDATE);
  const result = await decideFn(payload);
  const data = result.data ?? {};
  return {
    run: data.run ?? null,
    review: normalizeReviewRecord(data.review),
  };
}

export async function bulkApproveIngestion(payload) {
  const bulkFn = httpsCallable(functions, CALLABLE_BEER_BULK_APPROVE_INGESTION);
  const result = await bulkFn(payload);
  const data = result.data ?? {};
  return {
    run: data.run ?? null,
    review: normalizeReviewRecord(data.review),
    approvedKeys: Array.isArray(data.approvedKeys) ? data.approvedKeys : [],
    skippedKeys: Array.isArray(data.skippedKeys) ? data.skippedKeys : [],
  };
}

export async function bulkRejectIngestion(payload) {
  const bulkFn = httpsCallable(functions, CALLABLE_BEER_BULK_REJECT_INGESTION);
  const result = await bulkFn(payload);
  const data = result.data ?? {};
  return {
    run: data.run ?? null,
    review: normalizeReviewRecord(data.review),
    rejectedKeys: Array.isArray(data.rejectedKeys) ? data.rejectedKeys : [],
    skippedKeys: Array.isArray(data.skippedKeys) ? data.skippedKeys : [],
  };
}

export async function completeIngestionReview(runId) {
  const completeFn = httpsCallable(functions, CALLABLE_BEER_COMPLETE_INGESTION_REVIEW);
  const result = await completeFn({ runId });
  const data = result.data ?? {};
  return {
    run: data.run ?? null,
    review: normalizeReviewRecord(data.review),
  };
}

export async function applyIngestionReview(runId) {
  const applyFn = httpsCallable(
    functions,
    CALLABLE_BEER_APPLY_INGESTION_REVIEW,
    { timeout: 600000 },
  );
  const result = await applyFn({ runId });
  const data = result.data ?? {};
  return {
    run: data.run ?? null,
    review: normalizeReviewRecord(data.review),
    applyStatus: data.applyStatus ?? null,
    outcomes: Array.isArray(data.outcomes) ? data.outcomes : [],
    importResults: processImportResponse(data.importResults ?? {}),
  };
}

function normalizeFailureSummary(rawSummary) {
  if (!rawSummary || typeof rawSummary !== 'object') {
    return {
      open: 0,
      resolved: 0,
      autoPausedCount: 0,
      byCategory: {},
    };
  }
  return {
    open: Number(rawSummary.open) || 0,
    resolved: Number(rawSummary.resolved) || 0,
    autoPausedCount: Number(rawSummary.autoPausedCount) || 0,
    byCategory:
      rawSummary.byCategory && typeof rawSummary.byCategory === 'object'
        ? rawSummary.byCategory
        : {},
  };
}

function normalizeFailureItem(rawItem = {}) {
  return {
    ...rawItem,
    itemId: rawItem.itemId || rawItem.itemKey || rawItem.id || '',
    lastSuccessAt: parseIsoDate(rawItem.lastSuccessAt),
    openedAt: parseIsoDate(rawItem.openedAt),
    updatedAt: parseIsoDate(rawItem.updatedAt),
    resolvedAt: parseIsoDate(rawItem.resolvedAt),
    dispositionAt: parseIsoDate(rawItem.dispositionAt),
    autoPaused: rawItem.autoPaused === true,
  };
}

export async function listIngestionFailures(stateCode, options = {}) {
  const listFn = httpsCallable(functions, CALLABLE_BEER_LIST_INGESTION_FAILURES);
  const result = await listFn({
    stateCode,
    disposition: options.disposition ?? 'open',
    category: options.category,
    limit: options.limit,
  });
  const data = result.data ?? {};
  const rows = Array.isArray(data.rows) ? data.rows.map(normalizeFailureItem) : [];
  return {
    rows,
    summary: normalizeFailureSummary(data.summary),
  };
}

export async function resolveIngestionFailure(itemId) {
  const updateFn = httpsCallable(
    functions,
    CALLABLE_BEER_UPDATE_INGESTION_FAILURE_DISPOSITION,
  );
  const result = await updateFn({ itemId, disposition: 'resolved' });
  const data = result.data ?? {};
  return normalizeFailureItem(data.item);
}

export async function retryIngestionFailure(itemId) {
  const retryFn = httpsCallable(functions, CALLABLE_BEER_RETRY_INGESTION_FAILURE);
  const result = await retryFn({ itemId });
  const data = result.data ?? {};
  return {
    item: normalizeFailureItem(data.item),
    runId: data.runId ?? null,
    checkId: data.checkId ?? null,
  };
}
